import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { logError } from "@/lib/logError";
import { sessionRemove, purgeLegacyLocal } from "@/lib/sessionStore";
import { COLUMNS_BY_TAB, toCSV, downloadCSV, filterRows } from "@/components/admin/adminData";
import { LoginScreen } from "@/components/admin/LoginScreen";
import { AdminHeader, AdminTabs, AdminSearch, DataTable } from "@/components/admin/AdminParts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Auth lives in an httpOnly cookie set by the server; the browser never stores the key.
const LEGACY_KEY = "nc_admin_key";
const EMPTY = { camps: [], consultations: [], newsletter: [] };

const http = axios.create({ baseURL: API, withCredentials: true });

const fetchAdminData = async () => {
  const [camps, consultations, newsletter] = await Promise.all([
    http.get("/camp-registrations?limit=500"),
    http.get("/consultations?limit=500"),
    http.get("/newsletter?limit=1000"),
  ]);
  return { camps: camps.data, consultations: consultations.data, newsletter: newsletter.data };
};

const AdminPanel = ({ onLogout }) => {
  const [tab, setTab] = useState("camps");
  const [q, setQ] = useState("");
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  // Hook deps are complete: imports, module constants and state setters are stable.
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchAdminData());
    } catch (err) {
      logError("Admin.fetchAll", err);
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const activeCols = COLUMNS_BY_TAB[tab];
  const activeRows = data[tab];
  const filtered = useMemo(() => filterRows(activeRows, q), [activeRows, q]);

  const handleExport = () => {
    const csv = toCSV(filtered, activeCols);
    if (!csv) return toast.info("Nothing to export.");
    downloadCSV(tab, csv);
  };

  const tabs = [
    { k: "camps", label: "Camp Registrations", count: data.camps.length },
    { k: "consultations", label: "Consultations", count: data.consultations.length },
    { k: "newsletter", label: "Newsletter", count: data.newsletter.length },
  ];

  return (
    <div className="min-h-screen bg-[#0F1B2D] text-white">
      <Toaster position="top-center" richColors theme="dark" />
      <AdminHeader loading={loading} onRefresh={fetchAll} onExport={handleExport} onLogout={onLogout} />
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-8">
        <AdminTabs tabs={tabs} active={tab} onChange={setTab} />
        <AdminSearch q={q} onChange={setQ} shown={filtered.length} total={activeRows.length} />
        <div className="mt-8 pb-16">
          <DataTable rows={filtered} columns={activeCols} testIdPrefix={`admin-${tab}`} />
        </div>
      </div>
    </div>
  );
};

const loginErrorMessage = (err) => {
  const status = err?.response?.status;
  if (status === 401) return "Invalid admin key.";
  if (status === 429) return "Too many attempts. Please wait 15 minutes and try again.";
  return "Unable to reach server. Please try again.";
};

export default function Admin() {
  // null = checking, true = signed in, false = signed out
  const [authed, setAuthed] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    purgeLegacyLocal(LEGACY_KEY);
    sessionRemove(LEGACY_KEY);
    http
      .get("/admin/session")
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  const handleLogin = async (key) => {
    setError("");
    try {
      await http.post("/admin/login", { key });
      setAuthed(true);
    } catch (err) {
      logError("Admin.login", err);
      setError(loginErrorMessage(err));
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      await http.post("/admin/logout");
    } catch (err) {
      logError("Admin.logout", err);
    }
    setAuthed(false);
  }, []);

  if (authed === null) return <div className="min-h-screen bg-[#0F1B2D]" data-testid="admin-checking" />;
  if (!authed) return <LoginScreen onSubmit={handleLogin} error={error} />;
  return <AdminPanel onLogout={handleLogout} />;
}
